import { supabase, invokeFunction } from '@/lib/supabase'
import type { SocialAccount } from '@/types/domain'

export async function listAccounts(workspaceId: string): Promise<SocialAccount[]> {
  const { data, error } = await supabase
    .from('social_accounts')
    .select(
      'id, workspace_id, provider, external_id, username, display_name, avatar_url, parent_external_id, status, token_expires_at, last_synced_at, last_error, created_at',
    )
    .eq('workspace_id', workspaceId)
    .order('provider', { ascending: true })
    .order('display_name', { ascending: true })

  if (error) throw new Error(error.message)
  return (data ?? []) as SocialAccount[]
}

/** Uma conta que a Meta devolveu durante a conexão. */
export interface ConnectedAccountSummary {
  provider: string
  displayName: string
  externalId: string
  /** Para o Instagram: a Página do Facebook que o controla. */
  parentExternalId?: string | null
  username?: string | null
}

export interface ConnectResult {
  ok: boolean
  connected: number
  updated: number
  accounts: ConnectedAccountSummary[]
  live: boolean
  /**
   * Diagnóstico da Edge Function. Tudo aqui é opcional de propósito: o servidor
   * pode ainda não enviar esses campos, e a tela precisa funcionar dos dois
   * jeitos — sem eles, os números saem da própria lista de contas.
   */
  tokenType?: string
  pagesFound?: number
  instagramFound?: number
  hint?: string
}

/** Conecta via token de usuário da Meta (inclui o token permanente de teste). */
export function connectWithToken(workspaceId: string, accessToken: string): Promise<ConnectResult> {
  return invokeFunction<ConnectResult>('meta-oauth', { workspaceId, accessToken })
}

/** Conclui o fluxo OAuth padrão do Facebook Login. */
export function connectWithCode(
  workspaceId: string,
  code: string,
  redirectUri: string,
): Promise<ConnectResult> {
  return invokeFunction<ConnectResult>('meta-oauth', { workspaceId, code, redirectUri })
}

/**
 * Quantas Páginas e quantos perfis do Instagram o acesso enxergou. Prefere o
 * número do servidor; sem ele, conta o que veio na lista.
 */
export function countConnectedAccounts(result: ConnectResult): { pages: number; profiles: number } {
  const accounts = result.accounts ?? []
  return {
    pages: result.pagesFound ?? accounts.filter((item) => item.provider === 'facebook').length,
    profiles:
      result.instagramFound ?? accounts.filter((item) => item.provider === 'instagram').length,
  }
}

/* ------------------------------------------------------------------ *
 * Instagram pelo @ (perfis sem Página do Facebook vinculada)
 * ------------------------------------------------------------------ */

/** Perfil devolvido pela conexão direta, já gravado em `social_accounts`. */
export interface ConnectedInstagramAccount {
  /** Identificador da linha em `social_accounts`. */
  id: string
  provider: string
  externalId: string
  username: string
  displayName: string
  avatarUrl?: string | null
  /**
   * Contadores do `business_discovery`. Opcionais de propósito: a Meta nem
   * sempre os devolve, e a tela precisa funcionar sem eles.
   */
  followersCount?: number | null
  mediaCount?: number | null
}

export interface ConnectInstagramResult {
  ok: boolean
  /** `true` quando o perfil já estava no workspace e só teve os dados renovados. */
  alreadyConnected: boolean
  account: ConnectedInstagramAccount
}

/**
 * Conecta um perfil do Instagram pelo @, sem depender de Página do Facebook.
 *
 * A descoberta normal varre Página por Página, então um perfil Comercial que
 * não esteja vinculado a nenhuma Página nunca aparece — ainda que o token
 * tenha acesso direto a ele e possa publicar. Este caminho cobre esse caso.
 *
 * `handle` aceita `@usuario`, `usuario` ou o ID numérico.
 */
export function connectInstagramByHandle(
  workspaceId: string,
  handle: string,
): Promise<ConnectInstagramResult> {
  return invokeFunction<ConnectInstagramResult>('instagram-connect', { workspaceId, handle })
}

/**
 * O que a busca por contas novas conseguiu fazer nesta passada.
 *
 * Revalidar confere os tokens que já estão aqui; a busca pergunta à Meta se
 * apareceu alguma Página ou perfil depois da última conexão. São coisas
 * diferentes, e o servidor responde pelas duas separadamente.
 */
export interface SyncDiscovery {
  /**
   * `false` quando o workspace não tem credencial de busca guardada — o caso de
   * quem conectou antes desta mudança. É um estado esperado, não um erro.
   */
  attempted: boolean
  /** Contas que entraram agora. */
  connected: number
  /** Contas que já existiam e tiveram os dados renovados. */
  updated: number
  /** Nomes das contas novas, prontos para exibir. */
  newAccounts: string[]
}

export interface SyncResult {
  ok: boolean
  checked: Array<{ id: string; displayName: string; status: string; message?: string }>
  /**
   * Diagnóstico da busca. Opcional de propósito: um servidor anterior a esta
   * mudança responde só com `checked`, e a tela precisa funcionar assim também.
   */
  discovered?: SyncDiscovery
  /**
   * Explicação em português, pronta para exibir, de por que a busca não
   * aconteceu — sem credencial guardada ou com o acesso já expirado.
   */
  discoveryHint?: string | null
}

export function syncAccounts(workspaceId: string): Promise<SyncResult> {
  return invokeFunction<SyncResult>('accounts-sync', { workspaceId })
}

export async function disconnectAccount(accountId: string): Promise<void> {
  const { error } = await supabase.from('social_accounts').delete().eq('id', accountId)
  if (error) throw new Error(error.message)
}

/* ------------------------------------------------------------------ *
 * Login pelo Facebook (Facebook Login for Business)
 * ------------------------------------------------------------------ */

/** Permissões pedidas no diálogo e listadas no passo a passo do token colado. */
export const META_SCOPES = [
  'pages_show_list',
  'pages_manage_posts',
  'pages_read_engagement',
  'instagram_basic',
  'instagram_content_publish',
]

function readEnv(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

/** ID do app da Meta. Sem ele, só resta o caminho do token colado. */
const META_APP_ID = readEnv(import.meta.env.VITE_META_APP_ID)

/**
 * Configuração do Facebook Login for Business. Quando existe, ela substitui o
 * `scope` — a própria documentação da Meta pede para não enviar os dois juntos.
 */
const META_CONFIG_ID = readEnv(import.meta.env.VITE_META_CONFIG_ID)

/** Acompanha o `META_GRAPH_VERSION` das Edge Functions. */
const META_DIALOG_VERSION = readEnv(import.meta.env.VITE_META_GRAPH_VERSION) || 'v23.0'

/** O botão "Entrar com Facebook" só funciona com o App ID definido no build. */
export const isMetaLoginConfigured = META_APP_ID.length > 0

/**
 * A URL de retorno precisa estar cadastrada, caractere por caractere, em
 * Facebook Login for Business › Configurações › URIs de redirecionamento
 * válidos — e a troca do `code` usa exatamente esta mesma string.
 */
export function metaRedirectUri(): string {
  return `${window.location.origin}/contas/callback`
}

/** Valor antifalsificação (CSRF) que vai no diálogo e volta no redirecionamento. */
export function createMetaLoginState(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  // Contexto sem `crypto` (http fora de localhost): ainda assim, um valor único.
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

export interface MetaLoginHandshake {
  state: string
  workspaceId: string
  redirectUri: string
  startedAt: number
}

const HANDSHAKE_KEY = 'socialhub.meta-login'

export function saveMetaLoginHandshake(handshake: MetaLoginHandshake): void {
  try {
    sessionStorage.setItem(HANDSHAKE_KEY, JSON.stringify(handshake))
  } catch {
    // Sem sessionStorage não há como conferir o `state` na volta; o callback avisa.
  }
}

export function readMetaLoginHandshake(): MetaLoginHandshake | null {
  try {
    const raw = sessionStorage.getItem(HANDSHAKE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<MetaLoginHandshake>
    if (
      typeof parsed.state !== 'string' ||
      typeof parsed.workspaceId !== 'string' ||
      typeof parsed.redirectUri !== 'string'
    ) {
      return null
    }
    return {
      state: parsed.state,
      workspaceId: parsed.workspaceId,
      redirectUri: parsed.redirectUri,
      startedAt: typeof parsed.startedAt === 'number' ? parsed.startedAt : 0,
    }
  } catch {
    return null
  }
}

export function clearMetaLoginHandshake(): void {
  try {
    sessionStorage.removeItem(HANDSHAKE_KEY)
  } catch {
    // Nada a limpar quando o armazenamento está indisponível.
  }
}

/** Monta o diálogo de autorização da Meta para o navegador visitar. */
export function buildMetaAuthUrl(params: { state: string; redirectUri: string }): string {
  const url = new URL(`https://www.facebook.com/${META_DIALOG_VERSION}/dialog/oauth`)
  url.searchParams.set('client_id', META_APP_ID)
  url.searchParams.set('redirect_uri', params.redirectUri)
  url.searchParams.set('state', params.state)
  url.searchParams.set('response_type', 'code')

  if (META_CONFIG_ID) {
    url.searchParams.set('config_id', META_CONFIG_ID)
    // Faz o `response_type=code` vencer o padrão gravado na configuração.
    url.searchParams.set('override_default_response_type', 'true')
  } else {
    url.searchParams.set('scope', META_SCOPES.join(','))
  }

  return url.toString()
}
